import {Link, useLocation, useParams} from 'react-router-dom'
import {ChevronRight} from 'lucide-react'
import {useQuery} from '@tanstack/react-query'
import {routes} from '@/lib/routes'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {EXECUTION_KEY, JOB_FOR_EXECUTION_KEY} from '@/features/jobs/hooks/useExecution'

interface BreadcrumbSegment {
    label: string
    href?: string
}

export function JobBreadcrumbs() {
    const services = useServices()
    const location = useLocation()
    const params = useParams<{ id: string }>()
    const i18n = useI18n()

    const path = location.pathname

    const isJobDetail = path.match(/\/background-jobs\/jobs\/[^/]+$/)
    const isExecutionDetail = path.match(/\/background-jobs\/executions\/[^/]+$/)
    const isBuilderEdit = path.match(/\/background-jobs\/builder\/(?!new)[^/]+$/)
    const isBuilderNew = path.includes(routes.backgroundJobs.builder.newJob())

    // WHY: share the parent page's cache by reusing its query keys — useJob
    // and useExecution both populate ['job', id], and useExecution populates
    // ['execution', id]. TanStack Query dedupes on key, so only one network
    // request fires per (key, id) pair regardless of which component subscribes.
    const {data: job} = useQuery({
        queryKey: [JOB_FOR_EXECUTION_KEY, params.id],
        queryFn: () => services.jobs.detail(params.id!),
        enabled: (!!isJobDetail || !!isBuilderEdit) && !!params.id,
    })

    const {data: execution} = useQuery({
        queryKey: [EXECUTION_KEY, params.id],
        queryFn: () => services.executions.detail(params.id!),
        enabled: !!isExecutionDetail && !!params.id,
    })
    const {data: executionJob} = useQuery({
        queryKey: [JOB_FOR_EXECUTION_KEY, execution?.jobId],
        queryFn: () => services.jobs.detail(execution!.jobId),
        enabled: !!isExecutionDetail && !!execution?.jobId,
    })

    const segments: BreadcrumbSegment[] = [
        {label: i18n.backgroundJobs.title as string, href: routes.backgroundJobs.root()},
    ]

    if (path.includes('/dashboard')) {
        segments.push({label: i18n.backgroundJobs.dashboard as string})
    } else if (isBuilderNew) {
        segments.push({label: i18n.backgroundJobs.jobs as string, href: routes.backgroundJobs.jobs.list()})
        segments.push({label: i18n.jobBuilder.newJob as string})
    } else if (isBuilderEdit) {
        segments.push({label: i18n.backgroundJobs.jobs as string, href: routes.backgroundJobs.jobs.list()})
        if (params.id) {
            segments.push({label: job?.name || params.id, href: routes.backgroundJobs.jobs.detail(params.id)})
        }
        segments.push({label: i18n.jobBuilder.editTasks as string})
    } else if (isJobDetail) {
        segments.push({label: i18n.backgroundJobs.jobs as string, href: routes.backgroundJobs.jobs.list()})
        segments.push({label: job?.name || params.id || ''})
    } else if (path.includes('/jobs')) {
        segments.push({label: i18n.backgroundJobs.jobs as string})
    } else if (isExecutionDetail) {
        segments.push({label: i18n.backgroundJobs.executions as string, href: routes.backgroundJobs.executions.list()})
        // WHY: prefer the parent job's display name; fall back to "Execution #id"
        // while either query is in flight or has errored, so the breadcrumb stays
        // usable on slow networks / errors (issue 633).
        const leafLabel = executionJob?.name
            ?? (i18n.jobExecutions.executionIdLabel({id: params.id || ''}) as string)
        segments.push({label: leafLabel})
    } else if (path.includes('/executions')) {
        segments.push({label: i18n.backgroundJobs.executions as string})
    } else if (path.includes('/cluster')) {
        segments.push({label: i18n.backgroundJobs.cluster as string})
    }

    // Don't render if only root segment
    if (segments.length <= 1) return null

    return (
        <nav aria-label="breadcrumb" className="mb-4" data-testid="job-breadcrumbs">
            <ol className="flex items-center gap-1 text-sm text-muted-foreground">
                {segments.map((segment, index) => {
                    const isLast = index === segments.length - 1
                    return (
                        <li key={index} className="flex items-center gap-1">
                            {index > 0 && <ChevronRight className="h-3 w-3"/>}
                            {segment.href && !isLast ? (
                                <Link
                                    to={segment.href}
                                    className="hover:text-foreground transition-colors"
                                    data-testid={`breadcrumb-${index}`}
                                >
                                    {segment.label}
                                </Link>
                            ) : (
                                <span
                                    className={isLast ? 'text-foreground font-medium' : ''}
                                    data-testid={`breadcrumb-${index}`}
                                >
                                    {segment.label}
                                </span>
                            )}
                        </li>
                    )
                })}
            </ol>
        </nav>
    )
}
